// File: src/components/Admin/AdminHome/AdminHome.tsx
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users,
  Gavel,
  TrendingUp,
  Calendar,
  Clock,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Activity,
  BarChart3,
  IndianRupee,
  RefreshCw,
  Radio,
  ArrowRight,
  Trophy,
  type LucideIcon,
} from 'lucide-react';
import './AdminHome.css';
import LoadingGate from '../../Common/LoadingGate';
import adminService, { AdminDashboardData } from '../../../services/adminService';
import adminReportService, {
  AdminAuctionRow,
  AdminDashboardReport,
} from '../../../services/adminReportService';

const inr = new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 });

const activityMeta = (type: string): { Icon: LucideIcon; tone: string } => {
  switch (type) {
    case 'auction_created':
      return { Icon: Gavel, tone: 'blue' };
    case 'user_registered':
      return { Icon: Users, tone: 'violet' };
    case 'auction_completed':
      return { Icon: CheckCircle, tone: 'green' };
    case 'user_approved':
      return { Icon: CheckCircle, tone: 'blue' };
    case 'auction_cancelled':
      return { Icon: XCircle, tone: 'red' };
    default:
      return { Icon: Activity, tone: 'slate' };
  }
};

const formatTime = (t?: string | null) => {
  if (!t) return '';
  const [h, m] = t.split(':');
  const hh = parseInt(h, 10);
  return isNaN(hh) ? '' : `${hh % 12 || 12}:${m} ${hh >= 12 ? 'PM' : 'AM'}`;
};

const formatDate = (v?: string | null) => {
  if (!v) return '';
  const d = new Date(v.length === 10 ? `${v}T00:00:00` : v.replace(' ', 'T'));
  return isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
};

/* Compact 6-month chart: bars = auctions, line = bids (plain SVG) */
const MiniTrend: React.FC<{ data: AdminDashboardReport['monthly_trends'] }> = ({ data }) => {
  const W = 560;
  const H = 200;
  const pad = { l: 28, r: 10, t: 12, b: 26 };
  const innerW = W - pad.l - pad.r;
  const innerH = H - pad.t - pad.b;
  const max = Math.max(1, ...data.map((d) => Math.max(d.auctions, d.bids)));
  const top = Math.ceil(max / 4) * 4 || 4;
  const step = innerW / data.length;
  const barW = Math.min(28, step * 0.5);
  const y = (v: number) => pad.t + innerH - (v / top) * innerH;
  const x = (i: number) => pad.l + step * i + step / 2;
  const line = data.map((d, i) => `${i === 0 ? 'M' : 'L'}${x(i)},${y(d.bids)}`).join(' ');

  return (
    <div>
      <svg viewBox={`0 0 ${W} ${H}`} className="ahm-chart" role="img" aria-label="Auctions and bids per month">
        {[0, 1, 2, 3, 4].map((t) => {
          const v = (top / 4) * t;
          return (
            <g key={t}>
              <line x1={pad.l} x2={W - pad.r} y1={y(v)} y2={y(v)} className="ahm-gridline" />
              <text x={pad.l - 6} y={y(v) + 4} textAnchor="end" className="ahm-axis">
                {v}
              </text>
            </g>
          );
        })}
        {data.map((d, i) => (
          <g key={d.month}>
            <title>{`${d.month}: ${d.auctions} auctions, ${d.bids} bids`}</title>
            <rect x={x(i) - barW / 2} y={y(d.auctions)} width={barW} height={Math.max(0, pad.t + innerH - y(d.auctions))} rx={4} className="ahm-bar" />
            <text x={x(i)} y={H - 7} textAnchor="middle" className="ahm-axis">
              {d.month}
            </text>
          </g>
        ))}
        <path d={line} className="ahm-line" fill="none" />
        {data.map((d, i) => (
          <circle key={d.month} cx={x(i)} cy={y(d.bids)} r={3.2} className="ahm-dot" />
        ))}
      </svg>
      <div className="ahm-legend">
        <span><i className="ahm-sw ahm-sw-bar" /> Auctions</span>
        <span><i className="ahm-sw ahm-sw-line" /> Bids</span>
      </div>
    </div>
  );
};

const AdminHome: React.FC = () => {
  const navigate = useNavigate();

  const [core, setCore] = useState<AdminDashboardData | null>(null);
  const [report, setReport] = useState<AdminDashboardReport | null>(null);
  const [live, setLive] = useState<AdminAuctionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (initial: boolean) => {
    if (initial) setLoading(true);
    else setRefreshing(true);
    setError(null);

    const [coreRes, reportRes, liveRes] = await Promise.allSettled([
      adminService.getDashboard(),
      adminReportService.getDashboard('this_month'),
      adminReportService.getAuctions('all', 'live'),
    ]);

    if (coreRes.status === 'fulfilled') setCore(coreRes.value);
    else setError(coreRes.reason?.message || 'Failed to load dashboard');
    // the report panels are optional: if they fail the rest of the page still renders
    if (reportRes.status === 'fulfilled') setReport(reportRes.value);
    if (liveRes.status === 'fulfilled') setLive(liveRes.value.auctions.slice(0, 5));

    setLoading(false);
    setRefreshing(false);
  }, []);

  useEffect(() => {
    load(true);
    // keep the live numbers fresh without a manual reload
    const t = setInterval(() => load(false), 60000);
    return () => clearInterval(t);
  }, [load]);

  const o = core?.overview;
  const k = report?.kpis;

  const attention = useMemo(() => {
    const items: Array<{ tone: string; text: string; to: string }> = [];
    if (o && o.live_auctions > 0)
      items.push({ tone: 'green', text: `${o.live_auctions} auction${o.live_auctions > 1 ? 's' : ''} live right now`, to: '/admin/auctions' });
    if (o && o.pending_users > 0)
      items.push({ tone: 'orange', text: `${o.pending_users} user${o.pending_users > 1 ? 's' : ''} awaiting approval`, to: '/admin/users' });
    if (o && o.pending_auctions > 0)
      items.push({ tone: 'orange', text: `${o.pending_auctions} auction${o.pending_auctions > 1 ? 's' : ''} need review`, to: '/admin/auctions' });
    if (k && k.completed_without_bids > 0)
      items.push({ tone: 'slate', text: `${k.completed_without_bids} auction${k.completed_without_bids > 1 ? 's' : ''} closed with no bids this month`, to: '/admin/reports' });
    return items;
  }, [o, k]);

  const today = new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  if (error && !core) {
    return (
      <div className="ahm-error-state">
        <AlertTriangle />
        <h2>Couldn't load the dashboard</h2>
        <p>{error}</p>
        <button type="button" className="ahm-btn ahm-btn-primary" onClick={() => load(true)}>
          Try again
        </button>
      </div>
    );
  }

  const kpis: Array<{ label: string; value: React.ReactNode; hint: string; icon: LucideIcon; tone: string; to?: string }> = o
    ? [
        { label: 'Live Now', value: o.live_auctions, hint: 'Auctions currently bidding', icon: Radio, tone: 'green', to: '/admin/auctions' },
        { label: 'Upcoming', value: o.upcoming_auctions, hint: 'Scheduled to start', icon: Calendar, tone: 'blue', to: '/admin/auctions' },
        { label: 'Completed', value: o.completed_auctions, hint: `${o.cancelled_auctions} cancelled`, icon: CheckCircle, tone: 'violet', to: '/admin/auctions' },
        { label: 'Total Users', value: o.total_users.toLocaleString('en-IN'), hint: `+${o.new_users_7d} in the last 7 days`, icon: Users, tone: 'blue', to: '/admin/users' },
        { label: 'Total Bids', value: o.total_bids.toLocaleString('en-IN'), hint: `${o.total_participants} participants`, icon: TrendingUp, tone: 'orange', to: '/admin/reports' },
        {
          label: 'Deal Value',
          value: k ? inr.format(k.deal_value) : '-',
          hint: 'Winning bids this month',
          icon: IndianRupee,
          tone: 'green',
          to: '/admin/reports',
        },
      ]
    : [];

  const status = o
    ? [
        { label: 'Live', value: o.live_auctions, cls: 'ahm-c-green' },
        { label: 'Upcoming', value: o.upcoming_auctions, cls: 'ahm-c-blue' },
        { label: 'Completed', value: o.completed_auctions, cls: 'ahm-c-violet' },
        { label: 'Cancelled', value: o.cancelled_auctions, cls: 'ahm-c-red' },
      ]
    : [];
  const statusTotal = status.reduce((s, p) => s + p.value, 0);

  return (
    <LoadingGate ready={!loading}>
      <div className="ahm-root">
        {/* ---------- Hero ---------- */}
        <section className="ahm-hero">
          <div>
            <h1 className="ahm-title">
              <span className="ahm-title-icon">
                <BarChart3 />
              </span>
              Admin Dashboard
            </h1>
            <p className="ahm-subtitle">Platform overview &middot; {today}</p>
          </div>
          <div className="ahm-hero-actions">
            <button type="button" className="ahm-btn" onClick={() => navigate('/admin/users')}>
              <Users /> Users
            </button>
            <button type="button" className="ahm-btn" onClick={() => navigate('/admin/auctions')}>
              <Gavel /> Auctions
            </button>
            <button type="button" className="ahm-btn" onClick={() => navigate('/admin/reports')}>
              <BarChart3 /> Reports
            </button>
            <button type="button" className="ahm-btn ahm-btn-primary" onClick={() => load(false)} disabled={refreshing}>
              <RefreshCw className={refreshing ? 'ahm-spin' : ''} /> Refresh
            </button>
          </div>
        </section>

        {/* ---------- Needs attention ---------- */}
        {attention.length > 0 && (
          <section className="ahm-attention" aria-label="Needs attention">
            {attention.map((a) => (
              <button key={a.text} type="button" className={`ahm-attn ahm-attn-${a.tone}`} onClick={() => navigate(a.to)}>
                <i className="ahm-attn-dot" />
                <span>{a.text}</span>
                <ArrowRight />
              </button>
            ))}
          </section>
        )}

        {/* ---------- KPIs ---------- */}
        <section className="ahm-kpis">
          {kpis.map((c) => {
            const Icon = c.icon;
            return (
              <button key={c.label} type="button" className={`ahm-kpi ahm-tone-${c.tone}`} onClick={() => c.to && navigate(c.to)}>
                <span className="ahm-kpi-icon">
                  <Icon />
                </span>
                <span className="ahm-kpi-body">
                  <span className="ahm-kpi-label">{c.label}</span>
                  <span className="ahm-kpi-value">{c.value}</span>
                  <span className="ahm-kpi-hint">{c.hint}</span>
                </span>
              </button>
            );
          })}
        </section>

        {/* ---------- Trend + status ---------- */}
        <section className="ahm-grid ahm-grid-trend">
          <div className="ahm-card">
            <div className="ahm-card-head">
              <h2>Activity Trend</h2>
              <span>Last 6 months</span>
            </div>
            {report && report.monthly_trends.some((m) => m.auctions > 0 || m.bids > 0) ? (
              <MiniTrend data={report.monthly_trends.slice(-6)} />
            ) : (
              <div className="ahm-empty">
                <BarChart3 />
                <p>No activity to chart yet</p>
              </div>
            )}
          </div>

          <div className="ahm-card">
            <div className="ahm-card-head">
              <h2>Auctions by Status</h2>
              <span>{statusTotal} total</span>
            </div>
            <div className="ahm-split">
              {statusTotal > 0 &&
                status
                  .filter((s) => s.value > 0)
                  .map((s) => <span key={s.label} className={`ahm-split-seg ${s.cls}`} style={{ width: `${(s.value / statusTotal) * 100}%` }} />)}
            </div>
            <ul className="ahm-split-list">
              {status.map((s) => (
                <li key={s.label}>
                  <i className={`ahm-sw ${s.cls}`} />
                  <span>{s.label}</span>
                  <strong>{s.value}</strong>
                  <em>{statusTotal ? `${Math.round((s.value / statusTotal) * 100)}%` : '0%'}</em>
                </li>
              ))}
            </ul>
            {k && (
              <p className="ahm-note">
                Participation rate this month: <strong>{k.participation_rate}%</strong> &middot; Active bidders: <strong>{k.active_bidders}</strong>
              </p>
            )}
          </div>
        </section>

        {/* ---------- Live + upcoming ---------- */}
        <section className="ahm-grid">
          <div className="ahm-card">
            <div className="ahm-card-head">
              <h2>
                <Radio className="ahm-h-icon ahm-live-icon" /> Live Auctions
              </h2>
              <button type="button" className="ahm-link" onClick={() => navigate('/admin/auctions')}>
                View all <ArrowRight />
              </button>
            </div>
            {live.length === 0 ? (
              <div className="ahm-empty">
                <Radio />
                <p>No auctions are live right now</p>
              </div>
            ) : (
              <ul className="ahm-list">
                {live.map((a) => (
                  <li key={a.id}>
                    <button type="button" className="ahm-row" onClick={() => navigate(`/admin/auction-live/${a.id}`)}>
                      <span className="ahm-row-main">
                        <span className="ahm-row-title">{a.title}</span>
                        <span className="ahm-row-sub">
                          {a.auction_no} &middot; {a.creator_company || a.creator_name || '-'}
                        </span>
                      </span>
                      <span className="ahm-row-side">
                        <strong>{a.total_bids}</strong> bids
                        <small>{a.joined} joined</small>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="ahm-card">
            <div className="ahm-card-head">
              <h2>
                <Clock className="ahm-h-icon" /> Upcoming Auctions
              </h2>
              <span>Next to start</span>
            </div>
            {core && core.upcoming_auctions.length > 0 ? (
              <ul className="ahm-list">
                {core.upcoming_auctions.map((a) => (
                  <li key={a.id}>
                    <div className="ahm-row ahm-row-static">
                      <span className="ahm-row-main">
                        <span className="ahm-row-title">{a.title}</span>
                        <span className="ahm-row-sub">
                          {a.auctionNo} &middot; {a.company || '-'}
                        </span>
                        <span className="ahm-row-sub">Starts {a.startTime}</span>
                      </span>
                      <span className="ahm-row-side">
                        <strong>{a.participants}</strong> invited
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="ahm-empty">
                <Calendar />
                <p>No upcoming auctions</p>
              </div>
            )}
          </div>
        </section>

        {/* ---------- Activity + top bidders ---------- */}
        <section className="ahm-grid">
          <div className="ahm-card">
            <div className="ahm-card-head">
              <h2>
                <Activity className="ahm-h-icon" /> Recent Activity
              </h2>
            </div>
            {core && core.recent_activities.length > 0 ? (
              <ul className="ahm-timeline">
                {core.recent_activities.slice(0, 8).map((a, i) => {
                  const { Icon, tone } = activityMeta(a.type);
                  return (
                    <li key={`${a.type}-${a.id}-${i}`}>
                      <span className={`ahm-tl-icon ahm-tone-${tone}`}>
                        <Icon />
                      </span>
                      <div>
                        <p>{a.message}</p>
                        <small>{a.time_ago || a.formatted_time}</small>
                      </div>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <div className="ahm-empty">
                <Activity />
                <p>No recent activity</p>
              </div>
            )}
          </div>

          <div className="ahm-card">
            <div className="ahm-card-head">
              <h2>
                <Trophy className="ahm-h-icon" /> Top Bidders
              </h2>
              <span>This month</span>
            </div>
            {report && report.top_bidders.length > 0 ? (
              <ul className="ahm-list">
                {report.top_bidders.map((b, i) => (
                  <li key={b.id}>
                    <div className="ahm-row ahm-row-static">
                      <span className="ahm-rank">{i + 1}</span>
                      <span className="ahm-row-main">
                        <span className="ahm-row-title">{b.person_name || '-'}</span>
                        <span className="ahm-row-sub">{b.company_name || '-'}</span>
                      </span>
                      <span className="ahm-row-side">
                        <strong>{b.bids}</strong> bids
                        <small>{b.auctions} auctions</small>
                      </span>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="ahm-empty">
                <Trophy />
                <p>No bids placed this month</p>
              </div>
            )}
            {report && report.top_auctions.length > 0 && (
              <p className="ahm-note">
                Busiest auction: <strong>{report.top_auctions[0].title}</strong> ({report.top_auctions[0].total_bids} bids)
              </p>
            )}
          </div>
        </section>
      </div>
    </LoadingGate>
  );
};

export default AdminHome;
